import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, ArrowsClockwise, MagnifyingGlass, FilePdf, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";

export default function Transcripts() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [certsByStudent, setCertsByStudent] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/students");
      // Only show students who actually have certificates
      setStudents((data || []).filter((s) => s.certificates_count > 0));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const txt = q.trim().toLowerCase();
    if (!txt) return students;
    return students.filter((s) =>
      (s.student_no || "").toLowerCase().includes(txt) ||
      (s.full_name || "").toLowerCase().includes(txt) ||
      (s.nationality || "").toLowerCase().includes(txt)
    );
  }, [students, q]);

  const toggleExpand = async (sno) => {
    setExpanded((e) => ({ ...e, [sno]: !e[sno] }));
    if (!certsByStudent[sno]) {
      try {
        const { data } = await api.get(`/students/${encodeURIComponent(sno)}/certificates`);
        setCertsByStudent((c) => ({ ...c, [sno]: data || [] }));
      } catch (e) {
        toast.error("Sertifikalar yüklenemedi.");
      }
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="transcripts-page">
      <span className="label-eyebrow">/ Transkriptler</span>
      <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
        Öğrenci transkriptleri
      </h1>
      <p className="text-slate-600 mt-2 max-w-3xl">
        Her öğrencinin tüm eğitimlerini içeren <b>birleşik transkript</b> indirin, ya da
        her sertifikanın tek-eğitim transkriptini ayrı ayrı alın. Öğrenciler{" "}
        <Link to="/admin/students" className="underline text-[#0B2046]">Öğrenciler</Link> sayfasında yönetilir;
        sertifika atamak için <Link to="/admin/bulk-ops" className="underline text-[#0B2046]">Toplu İşlem</Link> ekranını kullanın.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="transcripts-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Öğrenci no, isim veya uyrukla ara…"
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2046]"
          />
        </div>
        <button onClick={load} className="px-3 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2 text-sm">
          <ArrowsClockwise size={14} /> Yenile
        </button>
      </div>

      <div className="mt-6 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-3 py-3 w-8"></th>
              <th className="px-4 py-3 text-left label-eyebrow">Öğrenci №</th>
              <th className="px-4 py-3 text-left label-eyebrow">Ad Soyad</th>
              <th className="px-4 py-3 text-left label-eyebrow">Uyruk</th>
              <th className="px-4 py-3 text-right label-eyebrow">Sertifika</th>
              <th className="px-4 py-3 text-right label-eyebrow">Toplam Saat</th>
              <th className="px-4 py-3 text-right label-eyebrow">Birleşik Transkript</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center label-eyebrow">Yükleniyor…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center label-eyebrow">Henüz sertifika atanmış öğrenci yok</td></tr>
            )}
            {filtered.map((s) => (
              <FragmentRow
                key={s.id}
                s={s}
                expanded={expanded[s.student_no]}
                toggleExpand={toggleExpand}
                certsByStudent={certsByStudent}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FragmentRow({ s, expanded, toggleExpand, certsByStudent }) {
  return (
    <>
      <tr className="border-t border-slate-200 hover:bg-slate-50">
        <td className="px-3 py-3">
          <button onClick={() => toggleExpand(s.student_no)} data-testid={`expand-${s.student_no}`}
            className="text-slate-500 hover:text-slate-900">
            {expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
          </button>
        </td>
        <td className="px-4 py-3 font-mono-plex font-bold">{s.student_no}</td>
        <td className="px-4 py-3">{s.full_name}</td>
        <td className="px-4 py-3">{s.nationality}</td>
        <td className="px-4 py-3 text-right font-mono-plex">{s.certificates_count}</td>
        <td className="px-4 py-3 text-right font-mono-plex">{s.total_hours} saat</td>
        <td className="px-4 py-3 text-right whitespace-nowrap">
          <a
            href={`${API_BASE}/students/${encodeURIComponent(s.student_no)}/transcript.pdf`}
            target="_blank" rel="noopener noreferrer"
            data-testid={`transcript-pdf-${s.student_no}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0B2046] text-white text-xs hover:bg-[#113066]"
          >
            <DownloadSimple size={13} /> Standart
          </a>
          <a
            href={`${API_BASE}/students/${encodeURIComponent(s.student_no)}/transcript-detailed.pdf`}
            target="_blank" rel="noopener noreferrer"
            data-testid={`transcript-detailed-pdf-${s.student_no}`}
            className="ml-1.5 inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white text-xs hover:bg-amber-600"
            title="Modül açıklamalarıyla beraber"
          >
            <DownloadSimple size={13} /> Detaylı
          </a>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/50">
          <td colSpan={7} className="px-12 py-3">
            <div className="text-xs label-eyebrow mb-2">Bu öğrenciye ait sertifikalar — her biri için ayrı transkript indirebilirsiniz:</div>
            {!certsByStudent[s.student_no] ? (
              <div className="text-xs text-slate-500">Yükleniyor…</div>
            ) : certsByStudent[s.student_no].length === 0 ? (
              <div className="text-xs text-slate-500">Sertifika yok.</div>
            ) : (
              <ul className="space-y-1.5">
                {certsByStudent[s.student_no].map((c) => (
                  <li key={c.id} className="flex items-center justify-between text-sm bg-white border border-slate-200 px-3 py-2">
                    <div className="flex-1">
                      <span className="font-mono-plex font-bold text-slate-900">{c.certificate_number}</span>
                      <span className="ml-3 text-slate-600">{c.training_program || c.license_type}</span>
                      <span className="ml-3 text-xs font-mono-plex text-slate-500">{c.total_training_hours} saat</span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap justify-end">
                      <a href={`${API_BASE}/certificates/${c.certificate_number}/pdf`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`cert-pdf-${c.certificate_number}`}
                        className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 text-xs hover:bg-slate-50">
                        <FilePdf size={13} /> Sertifika
                      </a>
                      <a href={`${API_BASE}/certificates/${c.certificate_number}/transcript.pdf`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`cert-transcript-${c.certificate_number}`}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[#0B2046] text-white text-xs hover:bg-[#113066]">
                        <DownloadSimple size={13} /> Transkript
                      </a>
                      <a href={`${API_BASE}/certificates/${c.certificate_number}/transcript-detailed.pdf`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`cert-transcript-detailed-${c.certificate_number}`}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500 text-white text-xs hover:bg-amber-600">
                        <DownloadSimple size={13} /> Detaylı Transkript
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
